import { useEffect, useState } from 'react';

/**
 * Estado de conectividade (PLAN §42).
 *
 * `navigator.onLine` só diz que existe uma interface de rede ativa — num wi-fi de
 * hotel sem internet ele reporta `true`. Por isso ele é usado apenas como indicador
 * barato para a UI; toda operação crítica deve tentar a requisição de verdade e cair
 * para o conteúdo local. Ver `probeConnectivity` para a verificação real.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = (): void => setOnline(true);
    const goOffline = (): void => setOnline(false);

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}

/**
 * Verificação real de conectividade: uma requisição pequena com timeout curto.
 *
 * O alvo é `/health.txt`, declarado como NetworkOnly no Service Worker de propósito.
 * Qualquer recurso em cache responderia "online" dentro de um avião, o que derrota o
 * ponto da sonda.
 *
 * Existe porque `navigator.onLine` mente: em wi-fi de hotel sem internet ele reporta
 * `true`, e em automação de navegador ele continua `true` mesmo com a rede cortada.
 * Use antes de qualquer operação que dependa mesmo da rede.
 */
export async function probeConnectivity(url = '/health.txt', timeoutMs = 3000): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;

  try {
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-store',
      signal: AbortSignal.timeout(timeoutMs),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Conectividade confirmada por sonda, e não só pelo `navigator.onLine`.
 *
 * `null` enquanto a primeira verificação não termina. Revalida quando o navegador
 * sinaliza mudança de rede e quando a aba volta ao primeiro plano — no iOS a PWA
 * fica suspensa e pode voltar num contexto de rede totalmente diferente.
 */
export function useReachability(enabled = true): boolean | null {
  const online = useOnlineStatus();
  const [reachable, setReachable] = useState<boolean | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const check = (): void => {
      if (!online) {
        setReachable(false);
        return;
      }
      void probeConnectivity().then((result) => {
        if (!cancelled) setReachable(result);
      });
    };

    check();

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, online]);

  return reachable;
}
